import { Component, OnInit, ViewChild } from '@angular/core';
import {Dashboard, GatewayStatus} from '../models/Dashboard';
import {DashboardService} from './dashboard.service';
import {MatTableDataSource, MatTableModule} from '@angular/material/table';
import {Contract} from '../models/Contract';
import {Router, RouterLink} from '@angular/router';
import {InvoiceNew} from '../models/InvoiceNew';
import {lastValueFrom} from "rxjs";
import {CurrencyPipe, DatePipe, CommonModule} from "@angular/common";
import {LoginService} from "../login/login.service";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {TrocaObrigatoriaSenhaComponent} from "../troca-obrigatoria-senha/troca-obrigatoria-senha.component";
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatCardModule,
    MatIconModule,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    MatMenuModule,
    MatPaginatorModule,
    RouterLink
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  @ViewChild(MatPaginator) set paginator(value: MatPaginator | undefined) {
    if (value) {
      this.invoiceDataSource.paginator = value;
    }
  }

  private readonly mockInvoices: InvoiceTableRow[] = [

  ];

  public readonly invoiceColumns: Array<string> = ['numero', 'descricao', 'vencimento', 'total', 'situacao',];

  public clientName: string = "";
  public dashboardStatus: Dashboard = {} as Dashboard;
  public invoiceDataSource: MatTableDataSource<InvoiceTableRow> = new MatTableDataSource<InvoiceTableRow>(this.mockInvoices);
  public contractRows: ContractCardRow[] = [];

  constructor(
    private service: DashboardService,
    private matDialog: MatDialog,
    private loginService: LoginService,
    private router: Router,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
  ) {}

  async ngOnInit(): Promise<any> {
    await this.verificarTrocaSenha();
    await this.getDashboardStatus();
  }
  private async verificarTrocaSenha(): Promise<void> {
    try {
      this.service.showSpinner();
      const teste = await lastValueFrom(this.loginService.verificarTrocaSenha());
      this.service.hideSpinner();
      if(teste){
        await lastValueFrom(this.matDialog.open(TrocaObrigatoriaSenhaComponent, {
          disableClose: true
        }).afterClosed());
      }
    } catch (e){
      console.log(e);
    } finally {
      this.service.hideSpinner();
    }
  }

  public gatewayStatus: GatewayStatus = {gatewayActive: false};

  /**
   * Recupera os dados do dashboard
   */
  async getDashboardStatus(): Promise<any> {
      try {
        this.service.showSpinner();
        const data: Dashboard = await lastValueFrom(this.service.getStatistics());
        this.gatewayStatus = await lastValueFrom(this.service.getGatewayActive());
        this.dashboardStatus = data;
        this.contractRows = this.mapContracts(data.CONTRATOS);
        this.invoiceDataSource.data = data.MOVIMENTACOES?.length ? this.mapInvoices(data.MOVIMENTACOES) : this.mockInvoices;
        this.clientName = data.NOME || '';
        if (data.INADIMPLENTE){
          this.service.toasterWarning('Parece que você tem alguns débitos em aberto. Em caso de dúvidas, procure nosso setor financeiro.', 'Aviso importante');
        }
      } catch (error: any){
        console.error(error);
        this.service.hideSpinner();
        this.service.toasterError(error.error);
      } finally {
        this.service.hideSpinner();
      }
  }

  /**
   * Aplica o filtro de contrato
   */
  applyFilterContract(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.contractRows = this.contractRows.filter(contract =>
      `${contract.id} ${contract.unidade} ${contract.dataLocacao} ${contract.valorBase}`.toLowerCase().includes(filterValue)
    );
  }

  /**
   * Aplica o filtro de boletos
   */
  applyFilterInvoice(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.invoiceDataSource.filter = filterValue.trim().toLowerCase();
  }

  /**
   *
   */
  async openMovement(id: string): Promise<any> {
    await this.router.navigate(['principal/faturas/' + id]);
  }

  /**
   * Abrir contrato
   */
  async openContract(id: string): Promise<any> {
    await this.router.navigate(['principal/contrato/' + id]);
  }

  /**
   * Cadastrar cartão de crédito
   */
  async cadastrarCartaoCredito(): Promise<any> {

    await this.router.navigate(['/principal/cadastro-cartao-credito']);
  }

  private mapInvoices(invoices: Array<InvoiceNew>): InvoiceTableRow[] {
    return invoices.map((invoice) => ({
      invoiceId: invoice.CODIGO_FATURA,
      numero: invoice.CODIGO_FATURA,
      descricao: invoice.DESCRICAO_FATURA,
      vencimento: this.datePipe.transform(invoice.DATA_VENCIMENTO, 'dd/MM/yyyy') ?? '--',
      total: this.currencyPipe.transform(invoice.VALOR_TOTAL, 'BRL', 'symbol', '1.2-2', 'pt-BR') ?? 'R$ 0,00',
      situacao: invoice.DESCRICAO_STATUS || 'Aguardando pagamento',
    }));
  }

  private mapContracts(contracts: Array<Contract>): ContractCardRow[] {
    return contracts.map((contract) => ({
      id: contract.GEN_CONTRATO,
      unidade: contract.DESCRICAO_LOJA || contract.DESCRICAO || 'Contrato ativo',
      dataLocacao: this.datePipe.transform(contract.DATA_LOCACAO, 'dd/MM/yyyy') ?? 'Data indisponível',
      valorBase: this.currencyPipe.transform(contract.VALOR_BASE, 'BRL', 'symbol', '1.2-2', 'pt-BR') ?? 'R$ 0,00',
      inadimplente: contract.INADIMPLENCIA,
    }));
  }
}

interface InvoiceTableRow {
  invoiceId: number;
  numero: number;
  descricao: string;
  vencimento: string;
  total: string;
  situacao: string;
}

interface ContractCardRow {
  id: number;
  unidade: string;
  dataLocacao: string;
  valorBase: string;
  inadimplente: boolean;
}
